import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Heart, Handshake, Mail, Send } from 'lucide-react';
import { scrollToSection } from '../utils/scrollToSection';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const GetInvolved = () => {
  const navigate = useNavigate();

  const handleScrollToSection = (path, sectionId) => {
    if (window.location.pathname === path) {
      scrollToSection(sectionId);
    } else {
      navigate(path);
      setTimeout(() => scrollToSection(sectionId), 500);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Get Involved
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Join our mission to bring healthcare to underserved communities worldwide
          </p>
        </div>
      </section>

      {/* Ways to Get Involved */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ways to Support Our Mission
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-blue-100 hover:border-blue-300 transition-colors shadow-lg hover:shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Volunteer
                </h3>
                <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Join our medical missions and make a direct impact on communities in need.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-100 hover:border-red-300 transition-colors shadow-lg hover:shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-6">
                  <Heart className="h-8 w-8 text-white fill-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Donate
                </h3>
                <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Your financial support helps us provide medical care and supplies to those who need it.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-100 hover:border-green-300 transition-colors shadow-lg hover:shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-6">
                  <Handshake className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Partner
                </h3>
                <p className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Organizations and businesses can collaborate with us to expand our reach and impact.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Volunteer Registration Form */}
      <section id="volunteer-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <Card className="border-none shadow-xl">
            <CardHeader className="text-center pb-8">
              <CardTitle className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Volunteer With Us
              </CardTitle>
              <CardDescription className="text-lg mt-2" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Click the button below to email us about volunteer opportunities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="bg-blue-50 p-8 rounded-lg text-center border-2 border-blue-100">
                  <Users className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Join Our Team
                  </h3>
                  <p className="text-gray-600 mb-6" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Email us to learn about current volunteer opportunities
                  </p>
                  <a href="mailto:info@medicsonmission.org?subject=Volunteer Application">
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-lg transition-all transform hover:scale-105"
                    >
                      <Send className="mr-2 h-5 w-5" />
                      Send message
                    </Button>
                  </a>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-bold text-lg text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Please include in your email:
                  </h4>
                  <ul className="space-y-2 text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Your full name and contact information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Your profession and relevant experience</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Your availability for missions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Why you want to volunteer with us</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Partnership Info */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Partner With Us
          </h2>
          <p className="text-lg text-gray-600 mb-8" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            We're always looking for organizations and businesses to partner with us. Whether you can provide medical supplies, logistical support, or funding, we'd love to hear from you.
          </p>
          <Button 
            onClick={() => handleScrollToSection('/contact', 'contact-form')}
            className="bg-green-600 hover:bg-green-700 text-white text-lg px-10 py-6 rounded-lg transition-all transform hover:scale-105"
          >
            Contact Us About Partnerships
          </Button>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;